import React from "react";
import {
  Typography,
  theme,
  Tag,
  Flex,
  Card,
  Divider,
} from "antd";
import {
  UserOutlined,
  MailOutlined,
  PhoneOutlined,
  IdcardOutlined,
  EnvironmentOutlined,
  SolutionOutlined,
} from "@ant-design/icons";
import type { ClientData } from "../types";

const { Title, Text } = Typography;

interface ClientDetailsProps {
  client: ClientData;
}

// ─── FieldItem ────────────────────────────────────────────────────────────────

const FieldItem = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) => {
  const { token } = theme.useToken();
  return (
    <Flex vertical gap={3}>
      <Text
        style={{
          fontSize: 11,
          fontWeight: 700,
          textTransform: "uppercase",
          letterSpacing: "0.06em",
          color: token.colorTextSecondary,
        }}
      >
        {label}
      </Text>
      {children}
    </Flex>
  );
};

// ─── SectionCard ─────────────────────────────────────────────────────────────

const SectionCard = ({
  icon,
  title,
  iconBg,
  iconColor,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  iconBg: string;
  iconColor: string;
  children: React.ReactNode;
}) => {
  const { token } = theme.useToken();
  return (
    <Card
      style={{ borderRadius: token.borderRadiusLG, height: "100%" }}
      styles={{ body: { padding: 24 } }}
      hoverable
    >
      <Flex vertical gap={18}>
        <Flex align="center" gap={12}>
          <Flex
            align="center"
            justify="center"
            style={{
              width: 40,
              height: 40,
              borderRadius: token.borderRadiusLG,
              background: iconBg,
              color: iconColor,
              fontSize: 18,
              flexShrink: 0,
            }}
          >
            {icon}
          </Flex>
          <Title level={5} style={{ margin: 0, fontWeight: 800, fontSize: 16 }}>
            {title}
          </Title>
        </Flex>
        <Divider style={{ margin: 0 }} />
        <Flex vertical gap={16}>
          {children}
        </Flex>
      </Flex>
    </Card>
  );
};

const ClientDetails: React.FC<ClientDetailsProps> = ({ client }) => {
  const { token } = theme.useToken();

  const fullName = `${client.client_name} ${client.client_lastname_1} ${client.client_lastname_2}`.trim();

  return (
    <div style={{ padding: "0 8px 32px" }}>
      <Flex vertical gap={24}>
        {/* ── Profile Header ── */}
        <Flex vertical align="center" gap={10} style={{ textAlign: "center" }}>
          <Title
            level={2}
            style={{
              margin: 0,
              fontWeight: 900,
              fontSize: 26,
              letterSpacing: "-0.02em",
              color: token.colorText,
            }}
          >
            {fullName || "Cliente sin nombre"}
          </Title>

          <Flex align="center" gap={10} wrap justify="center">
            <Tag
              color={client.client_type === "Moral" ? "purple" : "blue"}
              icon={<UserOutlined />}
              style={{
                fontWeight: 700,
                fontSize: 11,
                letterSpacing: "0.05em",
                textTransform: "uppercase",
                borderRadius: 999,
                margin: 0,
              }}
            >
              Persona {client.client_type}
            </Tag>
          </Flex>
        </Flex>

        {/* ── Info Cards ── */}
        <Flex gap={20} wrap style={{ alignItems: "stretch" }}>
          {/* Información Fiscal */}
          <div style={{ flex: "1 1 350px" }}>
            <SectionCard
              icon={<IdcardOutlined />}
              title="Información Fiscal"
              iconBg={token.colorPrimaryBg}
              iconColor={token.colorPrimary}
            >
              <FieldItem label="RFC">
                <Text
                  strong
                  copyable
                  style={{
                    fontSize: 16,
                    fontWeight: 900,
                    color: token.colorText,
                  }}
                >
                  {client.client_rfc}
                </Text>
              </FieldItem>

              <FieldItem label="Nombre / Razón Social">
                <Text style={{ color: token.colorText, lineHeight: 1.6 }}>
                  {client.client_company_name || fullName}
                </Text>
              </FieldItem>

              <FieldItem label="Régimen Fiscal">
                <Flex align="center" gap={6}>
                  <SolutionOutlined style={{ color: token.colorPrimary }} />
                  <Text style={{ color: token.colorText }}>
                    {client.client_tax_regime}
                  </Text>
                </Flex>
              </FieldItem>
            </SectionCard>
          </div>

          {/* Información de Contacto */}
          <div style={{ flex: "1 1 350px" }}>
            <SectionCard
              icon={<PhoneOutlined />}
              title="Información de Contacto"
              iconBg={token.colorSuccessBg}
              iconColor={token.colorSuccessText}
            >
              <FieldItem label="Correo Electrónico">
                <Flex align="center" gap={8}>
                  <MailOutlined style={{ color: token.colorTextSecondary }} />
                  <Text copyable style={{ color: token.colorText }}>
                    {client.client_email}
                  </Text>
                </Flex>
              </FieldItem>

              <FieldItem label="Teléfono">
                <Flex align="center" gap={8}>
                  <PhoneOutlined style={{ color: token.colorTextSecondary }} />
                  <Text style={{ color: token.colorText }}>
                    {client.client_phone}
                  </Text>
                </Flex>
              </FieldItem>

              <FieldItem label="Código Postal">
                <Flex align="center" gap={8}>
                  <EnvironmentOutlined
                    style={{ color: token.colorTextSecondary }}
                  />
                  <Text style={{ color: token.colorText }}>
                    {client.client_cp}
                  </Text>
                </Flex>
              </FieldItem>
            </SectionCard>
          </div>
        </Flex>
      </Flex>
    </div>
  );
};

export default ClientDetails;

